import React from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, CircleNotch, Sparkle, Trash, FileText } from "@phosphor-icons/react";
import { ReportRunner } from "./ReportRunner";

const SUGGESTIONS = [
  "GL balances for 201 Ledger, May-26",
  "Show me the trial balance report as Excel",
  "P&L report for FY 2026",
];

const AnalysisCard = ({ analysis }) => (
  <div className="mt-3 border border-slate-200 bg-white" data-testid="ai-analysis-card">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0B1E3F] text-white">
      <Sparkle size={15} weight="fill" className="text-[#C8A24B]" />
      <span className="text-[12px] font-semibold tracking-wide flex-1 truncate">
        AI Analysis — {analysis.report}
      </span>
      <span className="text-[10px] text-gray-300">
        {analysis.rows?.toLocaleString?.() ?? analysis.rows} rows{analysis.truncated ? " (sampled)" : ""}
      </span>
    </div>
    <div className="p-4">
      <p className="text-[14px] font-semibold text-[#0F172A] leading-snug" data-testid="ai-analysis-headline">
        {analysis.headline}
      </p>
      {(analysis.bullets || []).length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {analysis.bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-[12.5px] text-slate-700 leading-relaxed">
              <span className="text-[#C8A24B] font-bold flex-none">—</span>{b}
            </li>
          ))}
        </ul>
      )}
      {(analysis.top_items || []).length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {analysis.top_items.map((t, i) => (
            <div key={i} className="flex justify-between gap-3 text-[12px]">
              <span className="text-slate-500 truncate">{t.label}</span>
              <span className="font-semibold text-[#0B1E3F] whitespace-nowrap">{t.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

const ProposalCard = ({ proposal, onAnalyze }) => (
  <div className="mt-3 border border-slate-200 bg-white" data-testid="ai-proposal-card">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0B1E3F] text-white">
      <FileText size={15} weight="bold" className="text-[#C8A24B]" />
      <span className="text-[12px] font-semibold tracking-wide flex-1 truncate">{proposal.name}</span>
      <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-[#C8A24B] border border-[#C8A24B]/50 px-1.5 py-0.5">
        Run Plan
      </span>
    </div>
    {(proposal.resolved || []).length > 0 && (
      <div className="flex flex-wrap gap-1.5 px-4 pt-3">
        {proposal.resolved.map((r) => (
          <span key={r.name} data-testid={`ai-resolved-${r.name}`}
            className="text-[11px] bg-[#1F6B4A]/10 text-[#1F6B4A] border border-[#1F6B4A]/30 px-2 py-0.5 font-medium">
            {r.label}: {r.display}
          </span>
        ))}
      </div>
    )}
    <div className="p-4">
      <ReportRunner path={proposal.path} name={proposal.name}
        initialValues={proposal.values} initialFormat={proposal.format}
        onAnalyze={onAnalyze} />
    </div>
  </div>
);

export default function AiSection() {
  const [messages, setMessages] = React.useState([]);
  const [sessionId, setSessionId] = React.useState("");
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [analyzing, setAnalyzing] = React.useState(false);
  const endRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/reporting/ai/history").then((r) => {
      setMessages(r.data.messages || []);
      setSessionId(r.data.session_id || "");
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const r = await api.post("/reporting/ai/chat",
        { message: msg, session_id: sessionId }, { timeout: 180000 });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, proposal: r.data.proposal }]);
    } catch (e) {
      const err = e?.response?.data?.detail || "The AI request failed — please try again.";
      setMessages((m) => [...m, { role: "assistant", text: err }]);
      toast.error(err);
    } finally { setBusy(false); }
  };

  const analyze = async (proposal, parameters) => {
    if (analyzing) return;
    setAnalyzing(true);
    try {
      const r = await api.post("/reporting/ai/analyze",
        { path: proposal.path, name: proposal.name, parameters, session_id: sessionId },
        { timeout: 300000 });
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply, analysis: r.data.analysis }]);
    } catch (e) {
      const err = e?.response?.data?.detail || "Report analysis failed — please try again.";
      setMessages((m) => [...m, { role: "assistant", text: err }]);
      toast.error(err);
    } finally { setAnalyzing(false); }
  };

  const clear = async () => {
    await api.delete("/reporting/ai/history").catch(() => {});
    setMessages([]); setSessionId("");
  };

  return (
    <div className="max-w-4xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 180px)" }}>
      <div className="flex-1 space-y-4 pb-4" data-testid="ai-messages">
        {messages.length === 0 && !busy && (
          <div className="text-center pt-16" data-testid="ai-empty-state">
            <Sparkle size={32} weight="fill" className="text-[#C8A24B] mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-[#0F172A]">Ask for any Fusion report in plain English</h2>
            <p className="text-[13px] text-slate-500 mt-1 max-w-md mx-auto">
              I'll pick the right BI Publisher report, fill the parameters from your words and prepare it to run.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-6">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)}
                  data-testid={`ai-suggestion-${SUGGESTIONS.indexOf(s)}`}
                  className="text-[12px] border border-slate-300 text-slate-600 px-3 py-1.5 hover:border-[#C8A24B] hover:text-[#0B1E3F] transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
            {m.role === "user" ? (
              <div className="bg-[#0B1E3F] text-white px-4 py-2.5 text-[13px] max-w-[80%]"
                data-testid={`ai-msg-user-${i}`}>{m.text}</div>
            ) : (
              <div className="max-w-[92%]" data-testid={`ai-msg-assistant-${i}`}>
                <div className="flex items-start gap-2">
                  <Sparkle size={16} weight="fill" className="text-[#C8A24B] mt-1 flex-none" />
                  <div className="bg-white border border-slate-200 px-4 py-2.5 text-[13px] text-slate-700 leading-relaxed">
                    {m.text}
                  </div>
                </div>
                {m.proposal && (
                  <div className="ml-6">
                    <ProposalCard proposal={m.proposal}
                      onAnalyze={(vals) => analyze(m.proposal, vals)} />
                  </div>
                )}
                {m.analysis && <div className="ml-6"><AnalysisCard analysis={m.analysis} /></div>}
              </div>
            )}
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-[12px] text-slate-500 ml-6" data-testid="ai-thinking">
            <CircleNotch size={14} className="animate-spin" />
            Reading the catalog, matching parameters…
          </div>
        )}
        {analyzing && (
          <div className="flex items-center gap-2 text-[12px] text-slate-500 ml-6" data-testid="ai-analyzing">
            <CircleNotch size={14} className="animate-spin" />
            Running the report on Fusion and analyzing the output — this can take a minute…
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-0 bg-[#F8F9FA] pt-2 pb-1">
        <div className="flex items-center gap-2 bg-white border border-slate-300 focus-within:border-[#0B1E3F] px-3 py-1">
          <input
            data-testid="ai-chat-input"
            className="flex-1 py-2 text-[13px] outline-none bg-transparent"
            placeholder='e.g. "GL balances for 201 Ledger, May-26 as Excel"'
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            disabled={busy}
          />
          {messages.length > 0 && (
            <button onClick={clear} data-testid="ai-clear-btn" title="Clear conversation"
              className="text-slate-400 hover:text-red-600 transition-colors p-1">
              <Trash size={16} />
            </button>
          )}
          <button onClick={() => send()} disabled={busy || !input.trim()}
            data-testid="ai-send-btn"
            className="bg-[#0B1E3F] hover:bg-[#172E5D] disabled:opacity-40 text-white p-2 transition-colors">
            <PaperPlaneRight size={16} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
